import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus, Trash2, Users } from 'lucide-react';
import {
  adminFetchEventHighlights, adminCreateHighlightAssignments,
  adminPatchHighlightAssignment, adminDeleteHighlightAssignment, friendlyError,
} from '@/lib/api';
import { toast } from 'sonner';

export function AssignPlayersDialog({ open, onOpenChange, videoId, eventId, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]); // nuevas asignaciones
  const [saving, setSaving] = useState(false);

  const currentAssignments = useMemo(() => {
    if (!meta || !videoId) return [];
    const video = (meta.items || []).find((v) => v.id === videoId);
    return video?.assignments || [];
  }, [meta, videoId]);

  useEffect(() => {
    if (!open || !eventId) return;
    let alive = true;
    setLoading(true);
    adminFetchEventHighlights(eventId)
      .then((d) => { if (alive) { setMeta(d); setRows([{ player_profile_id: '', action_type: 'Otro', assignment_role: 'main', is_visible_in_public_portfolio: false }]); } })
      .catch((err) => { if (alive) toast.error(friendlyError(err)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, eventId]);

  const actions = meta?.action_types || ['Otro'];
  const players = meta?.eligible_players || [];

  const addRow = () => setRows((r) => [...r, { player_profile_id: '', action_type: 'Otro', assignment_role: 'secondary', is_visible_in_public_portfolio: false }]);
  const rmRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const updateRow = (i, patch) => setRows((r) => r.map((x, idx) => idx === i ? { ...x, ...patch } : x));

  const handleSave = async () => {
    const valid = rows.filter((r) => r.player_profile_id);
    if (valid.length === 0) { toast.error('Añade al menos un jugador.'); return; }
    if (valid.filter((r) => r.assignment_role === 'main').length !== 1) {
      toast.error('Debes marcar exactamente un jugador principal.');
      return;
    }
    setSaving(true);
    try {
      const res = await adminCreateHighlightAssignments(videoId, valid);
      toast.success(res.message || 'Asignaciones creadas.');
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(friendlyError(err)); }
    finally { setSaving(false); }
  };

  const handlePatch = async (id, patch) => {
    try {
      await adminPatchHighlightAssignment(id, patch);
      toast.success('Asignación actualizada.');
      const d = await adminFetchEventHighlights(eventId); setMeta(d);
      onSaved && onSaved();
    } catch (err) { toast.error(friendlyError(err)); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('¿Retirar esta asignación?')) return;
    try {
      await adminDeleteHighlightAssignment(id);
      toast.success('Asignación retirada.');
      const d = await adminFetchEventHighlights(eventId); setMeta(d);
      onSaved && onSaved();
    } catch (err) { toast.error(friendlyError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="assign-players-dialog" className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5" /> Asignar jugadores al highlight</DialogTitle>
          <DialogDescription>
            Solo se pueden asignar jugadores de los equipos participantes en el partido.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-[var(--ns-text-muted)]"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Cargando…</div>
        ) : !meta ? null : (
          <div className="flex flex-col gap-4">
            {/* Asignaciones ya existentes */}
            {currentAssignments.length > 0 ? (
              <div>
                <p className="text-xs uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold mb-2">Asignaciones actuales</p>
                <ul className="space-y-2">
                  {currentAssignments.map((a) => (
                    <li key={a.id} data-testid={`assignment-existing-${a.id}`} className="flex flex-wrap items-center gap-2 p-2 rounded-lg border border-[var(--ns-border)] bg-[var(--ns-bg-2)]/30">
                      <span className="text-sm font-semibold text-[var(--ns-text)] flex-1">
                        {a.player_first_name} {a.player_last_name}
                      </span>
                      <Badge className={`text-[10px] uppercase ${a.assignment_role === 'main' ? 'bg-[var(--ns-gold)]/20 text-[var(--ns-gold-700)]' : 'bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)]'}`}>{a.assignment_role}</Badge>
                      <span className="text-xs text-[var(--ns-text)] px-2">{a.action_type}</span>
                      <div className="flex items-center gap-1">
                        <Switch checked={a.is_visible_in_public_portfolio} onCheckedChange={(v) => handlePatch(a.id, { is_visible_in_public_portfolio: v })} data-testid={`assignment-visibility-${a.id}`} />
                        <span className="text-[10px] text-[var(--ns-text-muted)]">Público</span>
                      </div>
                      <Button variant="outline" size="sm" data-testid={`assignment-status-${a.id}`} className="h-7 text-[10px]" onClick={() => handlePatch(a.id, { status: a.status === 'active' ? 'hidden' : 'active' })}>
                        {a.status === 'active' ? 'Ocultar' : 'Reactivar'}
                      </Button>
                      <Button variant="ghost" size="sm" data-testid={`assignment-delete-${a.id}`} className="h-7 text-[var(--ns-red)] hover:bg-[var(--ns-red)]/8" onClick={() => handleDelete(a.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div>
              <p className="text-xs uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold mb-2">Nuevas asignaciones</p>
              <ul className="space-y-2">
                {rows.map((r, i) => (
                  <li key={i} data-testid={`assign-row-${i}`} className="flex flex-wrap items-center gap-2 p-2 rounded-lg border border-dashed border-[var(--ns-border-strong)]">
                    <div className="flex-1 min-w-[200px]">
                      <Label className="text-[10px] uppercase text-[var(--ns-text-muted)]">Jugador</Label>
                      <Select value={r.player_profile_id} onValueChange={(v) => updateRow(i, { player_profile_id: v })}>
                        <SelectTrigger className="h-9" data-testid={`assign-row-${i}-player`}><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                        <SelectContent>
                          {players.length === 0 ? <SelectItem value="__none__" disabled>Sin jugadores en los equipos</SelectItem> : players.map((p) => (
                            <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}{p.position ? ` · ${p.position}` : ''}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="min-w-[130px]">
                      <Label className="text-[10px] uppercase text-[var(--ns-text-muted)]">Rol</Label>
                      <Select value={r.assignment_role} onValueChange={(v) => updateRow(i, { assignment_role: v })}>
                        <SelectTrigger className="h-9" data-testid={`assign-row-${i}-role`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="main">Principal</SelectItem>
                          <SelectItem value="secondary">Secundario</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="min-w-[150px]">
                      <Label className="text-[10px] uppercase text-[var(--ns-text-muted)]">Acción</Label>
                      <Select value={r.action_type} onValueChange={(v) => updateRow(i, { action_type: v })}>
                        <SelectTrigger className="h-9" data-testid={`assign-row-${i}-action`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-1 pt-4">
                      <Switch checked={r.is_visible_in_public_portfolio} onCheckedChange={(v) => updateRow(i, { is_visible_in_public_portfolio: v })} data-testid={`assign-row-${i}-visible`} />
                      <span className="text-[10px] text-[var(--ns-text-muted)]">Público</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => rmRow(i)} className="h-9 text-[var(--ns-red)] hover:bg-[var(--ns-red)]/8 mt-4">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
              <Button variant="outline" size="sm" onClick={addRow} data-testid="assign-add-row" className="h-8 mt-2">
                <Plus className="h-3.5 w-3.5 mr-1" /> Añadir otro jugador
              </Button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="assign-players-save" className="bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar asignaciones'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
